import { Phone01Icon } from "@/components/Icons/Phone01Icon";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Info } from "@/design-system/Info";
import { Inline } from "@/design-system/Inline";
import { Stack } from "@/design-system/Stack";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";
import { useTranslation } from "@/translations";

import { Modal } from "../Modal/Modal";

interface PreviewLayoutProps {
  children: React.ReactNode;
  preview: React.ReactNode;
  hidePreview?: boolean;
}
export function WithAppPreviewLayout({ children, preview, hidePreview }: PreviewLayoutProps): React.ReactNode {
  const { t } = useTranslation();
  const [isPreviewOpen, previewOpenHandlers] = useBool();
  useKey("Escape", previewOpenHandlers.setFalse, isPreviewOpen);

  const previewSection = (
    <section className="flex flex-col items-center justify-start" onClick={previewOpenHandlers.setFalse}>
      <section className="flex w-full flex-col items-center justify-center gap-space-md">
        <aside className="relative w-full @md:w-compact-preview">{preview}</aside>
        <span className="sr-only flex items-center gap-space-sm text-center text-caption text-text-tertiary @md:not-sr-only">
          {t("component.preview-layout.title")}
          <Info tooltip={<span>{t("component.preview-layout.disclaimer")}</span>} />
        </span>
      </section>
    </section>
  );

  return (
    <Inline gap="md">
      <section className="relative flex flex-col">
        {children}
        {!hidePreview && (
          <aside className="mx-auto mt-space-md w-fit @md:hidden">
            <Button variant="ghostPrimary" onClick={previewOpenHandlers.setTrue} icon={<Phone01Icon />}>
              {t("component.preview-layout.btn.open-preview")}
            </Button>
          </aside>
        )}
      </section>
      {!hidePreview && (
        <>
          <Modal.Root
            title={t("component.preview-layout.title")}
            size="xl"
            isOpened={isPreviewOpen}
            onOpenChange={previewOpenHandlers.set}
          >
            <Modal.Body>
              <Stack gap="md">
                {previewSection}
                <span className="text-center text-caption text-text-secondary">
                  {t("component.preview-layout.disclaimer")}
                </span>
              </Stack>
            </Modal.Body>
          </Modal.Root>
          <aside className="hidden self-start @md:sticky @md:top-space-md @md:block @md:w-fit">
            <Card>{previewSection}</Card>
          </aside>
        </>
      )}
    </Inline>
  );
}
